import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';

import { useGetStampsBillingDate } from './hooks/use-get-stamps-billing-date';
import { useGetStampsPageData } from './hooks/use-get-stamps-page-data';
import messages from './messages';
import Global from '../../../../../../store/global-selectors';
import { actOnFreeStampsPlans } from '../../../../api/subscription';
import { pages } from '../../../../nav-items';
import SettingsPageWrapper from '../../../../settings-page-wrapper';
import { STAMPS_CORE_PLAN_PRICE } from '../../../constants';
import style from '../../../style.module.less';
import { BRIGHTBACK_CANCELLATION_QUERY_STRING } from '../brightback/hooks/use-get-brightback-data';

const STAMPS_WEBSITE = '//www.stamps.com/';

const preppedMessages = {
  headerMessage: messages.title,
  descriptionMessage: {
    ...messages.subtitle,
    values: { stampsPrice: STAMPS_CORE_PLAN_PRICE },
  },
};

const AVAILABLE_ACTIONS = {
  cancel: 'CancelPlans',
  keep: 'ConvertPlansSoTheyOutlastShipStationPlan',
};

const onFormUnmount = (didUserSubmitForm) => {
  if (!didUserSubmitForm) {
    actOnFreeStampsPlans(AVAILABLE_ACTIONS.cancel);
  }
};

const StampsAccountDecisionPage = () => {
  const history = useHistory();
  const [shouldCancel, setShouldCancel] = useState(true);
  const didUserSubmitForm = useRef(false);

  const navigateToSubscriptionPage = useCallback(
    () => history.push(`/${pages.settings.location}/${pages.paymentAndSubscription.location}${BRIGHTBACK_CANCELLATION_QUERY_STRING}`),
    [history],
  );

  const onSubmit = useCallback(() => {
    const cancelOrKeepDecision = shouldCancel ? AVAILABLE_ACTIONS.cancel : AVAILABLE_ACTIONS.keep;
    actOnFreeStampsPlans(cancelOrKeepDecision);
    didUserSubmitForm.current = true;
    navigateToSubscriptionPage();
  }, [navigateToSubscriptionPage, shouldCancel]);

  const toggleRadioItems = useCallback(() => {
    setShouldCancel((bool) => !bool);
  }, []);

  const { paymentMethodType, paymentMethodName, sellerStampsProviders, didError } = useGetStampsPageData();
  const planInfo = useSelector(Global.selectSellerPlanInfo);

  if (didError) {
    navigateToSubscriptionPage();
  }

  // If user doesn't submit the form before navigating away, we submit it for them
  useEffect(() => {
    return () => onFormUnmount(didUserSubmitForm.current);
  }, []);

  const { endDateTimeUTC } = planInfo ?? {};
  const { username, createDateUtc } = sellerStampsProviders.find((s) => s.username) ?? {};
  const stampsBillingDate = useGetStampsBillingDate(createDateUtc, endDateTimeUTC);

  if (!sellerStampsProviders.length) {
    return null;
  }

  return (
    <SettingsPageWrapper messages={preppedMessages}>
      <RadioCheckList className={style['stamps-account-decision-page']}>
        <RadioCheckListItem isChecked={!shouldCancel} onChecked={toggleRadioItems}>
          <FormattedMessage {...messages.keepMyStamps} values={{ stampsPrice: STAMPS_CORE_PLAN_PRICE }} />
        </RadioCheckListItem>
        <BannerMessageV2
          className={style['banner-message']}
          content={
            <Text
              formattedMessageValues={{
                username,
                paymentMethod: paymentMethodType,
                paymentMethodName,
                stampsBillingDate,
                a: (chunks) => (
                  <a href={STAMPS_WEBSITE} rel='noreferrer' target='_blank'>
                    {chunks}
                  </a>
                ),
              }}
              textType={TextType.Paragraph}
              value={messages.banner}
            />
          }
          status={BannerStatus.Warning}
        />
        <RadioCheckListItem className={style['cancel-accounts-radio']} isChecked={shouldCancel} onChecked={toggleRadioItems}>
          <FormattedMessage {...messages.cancelMyStamps} />
        </RadioCheckListItem>
      </RadioCheckList>

      <Button onClick={onSubmit} theme='primary'>
        <FormattedMessage {...messages.saveButton} />
      </Button>
    </SettingsPageWrapper>
  );
};

export default StampsAccountDecisionPage;
